import React, { createContext, useContext, useState } from 'react';

const ThemeContext = createContext();

export const LUMIRAE_DESIGN_TOKENS = {
  color: {
    bgApp: '#F7F1EA',
    surfaceBase: '#FFFDF8',
    surfaceRaised: '#FFFFFF',
    textPrimary: '#211B1D',
    textSecondary: '#6F6561',
    accentPrimary: '#9F1D22',
    accentSoft: '#F6D9D6',
    borderDefault: '#E8DDD2',
    stateError: '#B42318',
    stateSuccess: '#28734D',
  },
  spacing: {
    '2xs': 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 24,
    xl: 32,
    '2xl': 48,
    '3xl': 64,
  },
  radius: {
    sm: 10,
    md: 18,
    lg: 28,
    xl: 40,
    full: 999,
  },
};

const withDefaults = (theme) => ({
  cardBg: LUMIRAE_DESIGN_TOKENS.color.surfaceRaised,
  buttonText: '#ffffff',
  inputText: theme.text || LUMIRAE_DESIGN_TOKENS.color.textPrimary,
  secondaryText: theme.text,
  accent1: theme.accent,
  accent2: theme.cardBorder,
  light: theme.accentSoft,
  surface: theme.cardBg || LUMIRAE_DESIGN_TOKENS.color.surfaceBase,
  surfaceRaised: theme.cardBg || LUMIRAE_DESIGN_TOKENS.color.surfaceRaised,
  textMuted: theme.secondaryText || LUMIRAE_DESIGN_TOKENS.color.textSecondary,
  borderDefault: theme.cardBorder || LUMIRAE_DESIGN_TOKENS.color.borderDefault,
  stateError: LUMIRAE_DESIGN_TOKENS.color.stateError,
  stateSuccess: LUMIRAE_DESIGN_TOKENS.color.stateSuccess,
  radius: LUMIRAE_DESIGN_TOKENS.radius,
  spacing: LUMIRAE_DESIGN_TOKENS.spacing,
  ds: LUMIRAE_DESIGN_TOKENS,
  ...theme,
});

export const PREMIUM_THEME_ORDER = [
  'premium_memory_capsule',
  'pearl_rose',
  'warm_keepsake',
  'editorial_romance',
  'wedding_favor',
  'soft_family',
  'quiet_keepsake',
  'friends_graduation',
  'modern_poster',
  'memory_capsule',
  'playful_gift',
  'elegant_note',
];

export const TEMPLATE_THEME_MAP = {
  mom: 'pearl_rose',
  partner: 'editorial_romance',
  wedding: 'wedding_favor',
  birthday: 'pearl_rose',
  bestie: 'friends_graduation',
  graduation: 'friends_graduation',
  remembrance: 'quiet_keepsake',
  pet_memorial: 'quiet_keepsake',
  new_baby: 'soft_family',
  thank_you: 'pearl_rose',
};

const premiumThemes = {
  premium_memory_capsule: withDefaults({
    id: 'premium_memory_capsule',
    name: 'Premium Capsule',
    mood: 'Premium Memory Capsule',
    bg: '#F7F1EA',
    cardBg: '#FFFDF8',
    cardBorder: '#E8DDD2',
    cardShadow: 'rgba(33, 27, 29, 0.14)',
    accent: '#9F1D22',
    accentSoft: '#F6D9D6',
    accentMuted: '#E8DDD2',
    text: '#211B1D',
    secondaryText: '#6F6561',
    inputBg: '#FFFDF8',
    buttonBg: '#9F1D22',
    paper: '#FFFDF8',
  }),
  memory_capsule: withDefaults({
    id: 'memory_capsule',
    name: 'Memory Capsule',
    mood: 'Premium Memory Capsule',
    bg: '#eef5ff',
    cardBg: '#fbfdff',
    cardBorder: '#bfdbfe',
    cardShadow: '#93c5fd',
    accent: '#3b82f6',
    accentSoft: '#dbeafe',
    accentMuted: '#bfdbfe',
    text: '#26364f',
    secondaryText: '#708094',
    inputBg: '#f4f8ff',
    buttonBg: '#3b82f6',
    paper: '#ffffff',
  }),
  pearl_rose: withDefaults({
    id: 'pearl_rose',
    name: 'Pearl Rose',
    mood: 'Warm Keepsake',
    bg: '#F8F1ED',
    cardBg: '#FFFDF9',
    cardBorder: '#EADDD5',
    cardShadow: 'rgba(108, 74, 62, 0.16)',
    accent: '#B84A5C',
    accentSoft: '#F6DFDF',
    accentMuted: '#E8C8C9',
    text: '#271D1D',
    secondaryText: '#766762',
    inputBg: '#FFF9F5',
    buttonBg: '#B84A5C',
    paper: '#FFFDF9',
  }),
  warm_keepsake: withDefaults({
    id: 'warm_keepsake',
    name: 'Warm Keepsake',
    mood: "Mom / Mother's Day",
    bg: '#fff3ef',
    cardBg: '#fffdfb',
    cardBorder: '#ffd9cf',
    cardShadow: '#f7b9a8',
    accent: '#dc6b5a',
    accentSoft: '#ffe8e1',
    accentMuted: '#ffd1c5',
    text: '#52352f',
    secondaryText: '#8a6b62',
    inputBg: '#fff7f4',
    buttonBg: '#dc6b5a',
    paper: '#fffaf7',
  }),
  editorial_romance: withDefaults({
    id: 'editorial_romance',
    name: 'Editorial Romance',
    mood: 'Partner / Anniversary',
    bg: '#FBF4F1',
    cardBg: '#FFFAF7',
    cardBorder: '#E7D2CE',
    cardShadow: 'rgba(79, 42, 48, 0.16)',
    accent: '#8F2438',
    accentSoft: '#EAD0D6',
    accentMuted: '#DDB9C0',
    text: '#24191D',
    secondaryText: '#7D6B70',
    inputBg: '#FFF7F4',
    buttonBg: '#8F2438',
    paper: '#FFFAF7',
  }),
  wedding_favor: withDefaults({
    id: 'wedding_favor',
    name: 'Champagne Vow',
    mood: 'Wedding Favor',
    bg: '#F6F0E6',
    cardBg: '#FFFDF8',
    cardBorder: '#D8B7A4',
    cardShadow: 'rgba(143, 36, 56, 0.14)',
    accent: '#8F2438',
    accentSoft: '#F1D9D7',
    accentMuted: '#D8B7A4',
    text: '#211B1D',
    secondaryText: '#6F6561',
    inputBg: '#FFF9F4',
    buttonBg: '#8F2438',
    paper: '#FFFDF8',
    sage: '#7E8F7A',
    gold: '#C8A46A',
  }),
  playful_gift: withDefaults({
    id: 'playful_gift',
    name: 'Playful Gift',
    mood: 'Birthday',
    bg: '#fff7dd',
    cardBg: '#fffdf5',
    cardBorder: '#fde68a',
    cardShadow: '#fbbf24',
    accent: '#f97316',
    accentSoft: '#ffedd5',
    accentMuted: '#fed7aa',
    text: '#4a2f22',
    secondaryText: '#826450',
    inputBg: '#fff7ed',
    buttonBg: '#f97316',
    paper: '#fffaf0',
  }),
  modern_poster: withDefaults({
    id: 'modern_poster',
    name: 'Modern Poster',
    mood: 'Graduation',
    bg: '#F8F8F5',
    cardBg: '#ffffff',
    cardBorder: '#DCDDE2',
    cardShadow: 'rgba(22, 22, 22, 0.13)',
    accent: '#3157FF',
    accentSoft: '#DDE4FF',
    accentMuted: '#BFCBFF',
    text: '#161616',
    secondaryText: '#64666F',
    inputBg: '#F5F6FA',
    buttonBg: '#3157FF',
    paper: '#ffffff',
  }),
  friends_graduation: withDefaults({
    id: 'friends_graduation',
    name: 'Polaroid Map',
    mood: 'Friends / Graduation',
    bg: '#F8EFD9',
    cardBg: '#FFF8EA',
    cardBorder: '#D9BFA5',
    cardShadow: 'rgba(32, 55, 82, 0.16)',
    accent: '#BD583B',
    accentSoft: '#EBC6B5',
    accentMuted: '#8EA9BE',
    text: '#23282B',
    secondaryText: '#6E6E68',
    inputBg: '#FFF8EA',
    buttonBg: '#23476D',
    paper: '#FFF2D8',
    denim: '#23476D',
    terracotta: '#BD583B',
    cream: '#F8EFD9',
  }),
  quiet_keepsake: withDefaults({
    id: 'quiet_keepsake',
    name: 'Quiet Keepsake',
    mood: 'Remembrance / Memorial',
    bg: '#F6F4EF',
    cardBg: '#FFFEFA',
    cardBorder: '#DEDBD2',
    cardShadow: 'rgba(62, 63, 58, 0.13)',
    accent: '#6F8190',
    accentSoft: '#DDE5EA',
    accentMuted: '#C8D2D8',
    text: '#2F3230',
    secondaryText: '#72756F',
    inputBg: '#FAF8F3',
    buttonBg: '#6F8190',
    paper: '#FFFEFA',
  }),
  soft_family: withDefaults({
    id: 'soft_family',
    name: 'Soft Family',
    mood: 'New Baby / Family',
    bg: '#F7F4EE',
    cardBg: '#FFFFFB',
    cardBorder: '#DFE7DF',
    cardShadow: 'rgba(63, 96, 89, 0.13)',
    accent: '#7BA7A0',
    accentSoft: '#D9EBE6',
    accentMuted: '#C7DDD7',
    text: '#27313A',
    secondaryText: '#66736F',
    inputBg: '#FAFCF8',
    buttonBg: '#7BA7A0',
    paper: '#FFFFFB',
  }),
  elegant_note: withDefaults({
    id: 'elegant_note',
    name: 'Elegant Note',
    mood: 'Thank You / Appreciation',
    bg: '#fff8ec',
    cardBg: '#fffdfa',
    cardBorder: '#ead7b7',
    cardShadow: '#d6b98a',
    accent: '#b7791f',
    accentSoft: '#fef3c7',
    accentMuted: '#fde68a',
    text: '#443522',
    secondaryText: '#7a6850',
    inputBg: '#fffbeb',
    buttonBg: '#b7791f',
    paper: '#fffdfa',
  }),
};

const legacyThemes = {
  soft_serenity: withDefaults({
    id: 'soft_serenity',
    name: 'Rose',
    mood: 'Legacy',
    bg: '#fdf2f8',
    cardBg: '#ffffff',
    cardBorder: '#fce7f3',
    cardShadow: '#fbcfe8',
    accent: '#f472b6',
    accentSoft: '#fdf2f8',
    accentMuted: '#fce7f3',
    text: '#5A4A6B',
    secondaryText: '#84758f',
    inputBg: '#fdf2f8',
    buttonBg: '#f472b6',
  }),
  warm_sunrise: withDefaults({
    id: 'warm_sunrise',
    name: 'Sunrise',
    mood: 'Legacy',
    bg: '#fff7ed',
    cardBg: '#ffffff',
    cardBorder: '#fed7aa',
    cardShadow: '#fdba74',
    accent: '#f97316',
    accentSoft: '#ffedd5',
    accentMuted: '#fed7aa',
    text: '#4a2f22',
    secondaryText: '#826450',
    inputBg: '#fff7ed',
    buttonBg: '#f97316',
  }),
  garden_bloom: withDefaults({
    id: 'garden_bloom',
    name: 'Garden',
    mood: 'Legacy',
    bg: '#f0fdf4',
    cardBg: '#ffffff',
    cardBorder: '#bbf7d0',
    cardShadow: '#86efac',
    accent: '#22c55e',
    accentSoft: '#dcfce7',
    accentMuted: '#bbf7d0',
    text: '#244233',
    secondaryText: '#65786a',
    inputBg: '#f0fdf4',
    buttonBg: '#22c55e',
  }),
  ocean_keepsake: withDefaults({
    id: 'ocean_keepsake',
    name: 'Ocean',
    mood: 'Legacy',
    bg: '#eff6ff',
    cardBg: '#ffffff',
    cardBorder: '#bfdbfe',
    cardShadow: '#93c5fd',
    accent: '#3b82f6',
    accentSoft: '#dbeafe',
    accentMuted: '#bfdbfe',
    text: '#26364f',
    secondaryText: '#708094',
    inputBg: '#eff6ff',
    buttonBg: '#3b82f6',
  }),
  lavender_dream: withDefaults({
    id: 'lavender_dream',
    name: 'Lavender',
    mood: 'Legacy',
    bg: '#faf5ff',
    cardBg: '#ffffff',
    cardBorder: '#e9d5ff',
    cardShadow: '#d8b4fe',
    accent: '#a855f7',
    accentSoft: '#f3e8ff',
    accentMuted: '#e9d5ff',
    text: '#44315c',
    secondaryText: '#786885',
    inputBg: '#faf5ff',
    buttonBg: '#a855f7',
  }),
  cyber_pop: withDefaults({
    id: 'cyber_pop',
    name: 'Cyber Pop',
    mood: 'Legacy',
    bg: '#0f1028',
    cardBg: '#181a3f',
    cardBorder: '#67e8f9',
    cardShadow: '#ec4899',
    accent: '#22d3ee',
    accentSoft: '#1e1b4b',
    accentMuted: '#334155',
    text: '#f8fafc',
    secondaryText: '#cbd5e1',
    inputBg: '#111827',
    buttonBg: '#ec4899',
  }),
  dark_minimal: withDefaults({
    id: 'dark_minimal',
    name: 'Noir',
    mood: 'Legacy',
    bg: '#111111',
    cardBg: '#1f1f1f',
    cardBorder: '#3f3f46',
    cardShadow: '#000000',
    accent: '#f5f5f5',
    accentSoft: '#27272a',
    accentMuted: '#52525b',
    text: '#f4f4f5',
    secondaryText: '#a1a1aa',
    inputBg: '#18181b',
    buttonBg: '#f4f4f5',
    buttonText: '#111111',
  }),
  retro_pop: withDefaults({
    id: 'retro_pop',
    name: 'Retro Pop',
    mood: 'Legacy',
    bg: '#fff1db',
    cardBg: '#fffaf0',
    cardBorder: '#1f2937',
    cardShadow: '#facc15',
    accent: '#ef4444',
    accentSoft: '#fde68a',
    accentMuted: '#f97316',
    text: '#1f2937',
    secondaryText: '#6b7280',
    inputBg: '#fff7ed',
    buttonBg: '#2563eb',
  }),
};

export const THEMES = {
  ...premiumThemes,
  ...legacyThemes,
};

export const THEME_SELECTOR_ORDER = [
  ...PREMIUM_THEME_ORDER,
  ...Object.keys(legacyThemes),
];

export const ThemeProvider = ({ children }) => {
  const [currentThemeId, setCurrentThemeId] = useState('pearl_rose');
  const theme = THEMES[currentThemeId] || THEMES.pearl_rose;

  return (
    <ThemeContext.Provider value={{ theme, currentThemeId, setCurrentThemeId }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
